import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useTranslation } from '@/i18n'
import { useAuth } from '@/features/auth/hooks/useAuth'

import { useUserChallenges } from '../UserChallengeProvider'
import { Button } from '@/components/Button'
import { Challenge } from '@/types/challenge'
import { StreakDisplay, StreakBadge } from '@/components/StreakDisplay'
import { StreakCalendar } from '@/components/StreakCalendar'
import { ChallengeStatistics, ProgressChart } from '@/components/ChallengeStatistics'
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton'

interface DayValidation {
  date: Date
  isToday: boolean
  isYesterday: boolean
  isTomorrow: boolean
  canValidate: boolean
  isValidated: boolean
  validationTime?: string
  unavailableReason?: string
}

export function ChallengeDetailsPage() {
  const { challengeId } = useParams<{ challengeId: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { challengeRepository } = useUserChallenges()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  
  const [isValidating, setIsValidating] = useState(false)

  const { data: challenge, isLoading, error } = useQuery({
    queryKey: ['challenge', challengeId],
    queryFn: () => challengeRepository.findById(challengeId!),
    enabled: !!challengeId && !authLoading, // Wait for auth to complete
  })

  const validateDayMutation = useMutation({
    mutationFn: async (day: 'yesterday' | 'today') => {
      if (!challenge) throw new Error('Challenge not found')
      
      const now = new Date()
      const streakDate = day === 'yesterday' 
        ? new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : now.toISOString().split('T')[0]
      
      // Use repository method to validate check-in
      return challengeRepository.validateCheckIn(challenge.id, streakDate)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenge', challengeId] })
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      toast.success(t('challenges.dayValidated'))
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : t('errors.failedToValidateDay')
      if (errorMessage.includes('already been validated')) {
        toast.error(t('challenges.alreadyValidated'))
      } else {
        toast.error(errorMessage)
      }
    },
    onSettled: () => {
      setIsValidating(false)
    }
  })

  const handleValidateDay = (day: 'yesterday' | 'today') => {
    if (isValidating) return // Prevent multiple clicks
    
    // Additional client-side validation
    if (challenge) {
      const now = new Date()
      const checkDate = day === 'yesterday' 
        ? new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : now.toISOString().split('T')[0]
      
      const validatedDays = challenge.validatedDays || []
      if (validatedDays.includes(checkDate)) {
        toast.error(t('challenges.alreadyValidated'))
        return
      }
    }
    
    setIsValidating(true)
    validateDayMutation.mutate(day)
  }

  const formatFrequency = (challenge: Challenge) => {
    const { frequency } = challenge
    
    switch (frequency.mode) {
      case 'daily':
        return `Daily at ${frequency.times?.join(', ') || 'not set'}`
      case 'hourly':
        return `Every hour from ${frequency.startHour || 9}:00 to ${frequency.endHour || 18}:00`
      case 'weekly':
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        const selectedDays = frequency.daysOfWeek?.map(d => days[d]).join(', ') || 'none'
        return `${selectedDays} at ${frequency.times?.join(', ') || 'not set'}`
      default:
        return t('challenges.customFrequency')
    }
  }

  const canValidateToday = (challenge: Challenge) => {
    if (!challenge.frequency.times || challenge.frequency.times.length === 0) {
      return true // If no specific times, allow validation anytime
    }

    const now = new Date()
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    
    // For daily challenges, check if we're past the last notification time
    if (challenge.frequency.mode === 'daily') {
      const lastTime = challenge.frequency.times[challenge.frequency.times.length - 1]
      return currentTime >= lastTime
    }
    
    // For hourly challenges, check if we're past the end hour
    if (challenge.frequency.mode === 'hourly') {
      const endHour = challenge.frequency.endHour || 18
      return now.getHours() >= endHour
    }
    
    // For weekly challenges, check if today is a scheduled day and past the last time
    if (challenge.frequency.mode === 'weekly') {
      const today = now.getDay()
      if (!challenge.frequency.daysOfWeek?.includes(today)) {
        return false // Not a scheduled day
      }
      const lastTime = challenge.frequency.times[challenge.frequency.times.length - 1]
      return currentTime >= lastTime
    }
    
    return false
  }

  const getDayValidations = (challenge: Challenge): DayValidation[] => {
    const now = new Date()
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
    
    // Convert dates to YYYY-MM-DD format for comparison
    const todayDateStr = now.toISOString().split('T')[0]
    const yesterdayDateStr = yesterday.toISOString().split('T')[0]
    
    // Check if days are validated using the validatedDays array
    const validatedDays = challenge.validatedDays || []
    const hasCheckedInToday = validatedDays.includes(todayDateStr)
    const hasCheckedInYesterday = validatedDays.includes(yesterdayDateStr)
    
    // Check if challenge was created before yesterday
    const challengeCreatedDate = new Date(challenge.createdAt)
    const wasActiveYesterday = challengeCreatedDate.getTime() <= yesterday.getTime()
    
    return [
      {
        date: yesterday,
        isToday: false,
        isYesterday: true,
        isTomorrow: false,
        canValidate: !hasCheckedInYesterday && wasActiveYesterday,
        isValidated: !!hasCheckedInYesterday,
        validationTime: hasCheckedInYesterday ? challenge.lastCheckIn : undefined,
        unavailableReason: hasCheckedInYesterday ? undefined : (!wasActiveYesterday ? t('stats.notActive') : undefined)
      },
      {
        date: now,
        isToday: true,
        isYesterday: false,
        isTomorrow: false,
        canValidate: !hasCheckedInToday && canValidateToday(challenge),
        isValidated: !!hasCheckedInToday,
        validationTime: hasCheckedInToday ? challenge.lastCheckIn : undefined,
        unavailableReason: hasCheckedInToday ? undefined : (!canValidateToday(challenge) ? t('stats.waitForTime') : undefined)
      },
      {
        date: tomorrow,
        isToday: false,
        isYesterday: false,
        isTomorrow: true,
        canValidate: false,
        isValidated: false,
        unavailableReason: t('stats.upcoming')
      }
    ]
  }

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-bg">
        <div className="max-w-4xl mx-auto px-lg py-2xl">
          <div className="flex items-center gap-md mb-2xl">
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" className="h-8" width={200} />
          </div>
          
          <div className="bg-surface rounded-xl border border-border shadow-sm p-xl mb-xl">
            <div className="flex items-start gap-xl">
              <Skeleton variant="rounded" width={120} height={140} />
              <div className="flex-1">
                <Skeleton variant="text" className="h-8 mb-4" width="60%" />
                <SkeletonText lines={3} className="mb-4" />
                <Skeleton variant="rounded" width={100} height={32} />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-xl">
            <Skeleton variant="rounded" height={200} />
            <Skeleton variant="rounded" height={200} />
          </div>
        </div>
      </div>
    )
  }

  if (!authLoading && !isLoading && (error || !challenge)) {
    return (
      <div className="min-h-screen bg-bg">
        <div className="max-w-4xl mx-auto px-lg py-2xl">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-text mb-lg">{t('challenges.challengeNotFound')}</h1>
            <p className="text-text-muted mb-xl">{t('challenges.challengeNotFoundDescription')}</p>
            <Link to="/">
              <Button>{t('common.goHome')}</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const dayValidations = getDayValidations(challenge)

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-4xl mx-auto px-lg py-2xl">
        {/* Header */}
        <div className="flex items-center gap-md mb-2xl">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-surface border border-border hover:bg-bg-alt transition-colors"
          >
            <svg className="w-5 h-5 text-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-3xl font-bold text-text">Challenge Details</h1>
        </div>

        {/* Challenge Info */}
        <div className="bg-surface rounded-xl border border-border shadow-sm p-xl mb-xl">
          <div className="flex items-start gap-xl">
            {/* Streak Display */}
            <div className="flex flex-col items-center justify-center text-center min-w-[120px] bg-gradient-to-b from-primary/5 to-primary/10 rounded-xl px-lg py-xl border border-primary/10">
              <div className="text-6xl font-bold text-primary mb-sm leading-none">
                {challenge.currentStreak}
              </div>
              <div className="text-sm text-text-muted uppercase tracking-wide font-medium mb-sm">
                Day Streak
              </div>
              {challenge.bestStreak > 0 && challenge.bestStreak > challenge.currentStreak && (
                <div className="text-sm text-text-faint bg-surface px-md py-xs rounded-lg">
                  Best: {challenge.bestStreak}
                </div>
              )}
            </div>
            
            {/* Challenge Details */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-text mb-md">{challenge.title}</h2>
              <div className="space-y-sm text-text-muted">
                <p className="flex items-center gap-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {formatFrequency(challenge)}
                </p>
                <p className="flex items-center gap-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {challenge.totalCheckIns} total check-ins
                </p>
                <p className="flex items-center gap-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {challenge.participantsCount} participant{challenge.participantsCount !== 1 ? 's' : ''}
                </p>
              </div>
              
              {/* Status Badge */}
              <div className="mt-lg">
                <span className={`inline-flex items-center px-md py-xs rounded-lg text-sm font-medium ${
                  challenge.status === 'active' 
                    ? 'bg-success/10 text-success border border-success/20'
                    : challenge.status === 'paused'
                    ? 'bg-warning/10 text-warning border border-warning/20'
                    : 'bg-text-muted/10 text-text-muted border border-text-muted/20'
                }`}>
                  {challenge.status === 'active' && '● Active'}
                  {challenge.status === 'paused' && '⏸ Paused'}
                  {challenge.status === 'completed' && '✓ Completed'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Streak Stats and Calendar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-xl mb-xl">
          <div className="bg-surface rounded-xl border border-border shadow-sm p-xl">
            <h3 className="text-xl font-semibold text-text mb-lg">{t('challenges.streakStats')}</h3>
            <StreakDisplay 
              currentStreak={challenge.currentStreak || 0}
              longestStreak={challenge.bestStreak || 0}
              lastCheckIn={challenge.lastCheckIn ? new Date(challenge.lastCheckIn) : null}
              size="lg"
              className="justify-center"
            />
            {challenge.currentStreak > 0 && (
              <div className="mt-lg flex justify-center">
                <StreakBadge streak={challenge.currentStreak} size="lg" />
              </div>
            )}
          </div>
          
          <StreakCalendar 
            checkIns={(challenge.validatedDays || []).map(d => new Date(d))}
            className="shadow-sm"
          />
        </div>
        
        {/* Statistics and Progress */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-xl mb-xl">
          <ChallengeStatistics challenge={challenge} />
          <ProgressChart challenge={challenge} />
        </div>

        {/* Streak Validation Section */}
        {challenge.status === 'active' && (
          <div className="bg-surface rounded-xl border border-border shadow-sm p-xl">
            <h3 className="text-xl font-semibold text-text mb-lg">{t('challenges.validateProgress')}</h3>
            <p className="text-text-muted mb-xl">
              Complete your daily challenge to maintain your streak. You can only validate after your scheduled time range.
            </p>
            
            {/* Days Row */}
            <div className="grid grid-cols-3 gap-md">
              {dayValidations.map((day, index) => {
                const dayName = day.isYesterday ? t('stats.yesterday') : day.isToday ? t('stats.today') : t('stats.tomorrow')
                const dateStr = day.date.toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric' 
                })
                
                return (
                  <div
                    key={index}
                    className={`relative p-lg rounded-xl border-2 text-center transition-all ${
                      day.isToday
                        ? 'border-primary bg-primary/5'
                        : day.isValidated
                        ? 'border-success bg-success/5'
                        : 'border-border bg-bg-alt/50'
                    }`}
                  >
                    <div className="text-sm font-medium text-text-muted mb-xs">{dayName}</div>
                    <div className="text-lg font-semibold text-text mb-md">{dateStr}</div>
                    
                    {day.isValidated ? (
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-success flex items-center justify-center mb-sm">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-sm text-success font-medium">{t('challenges.completed')}</span>
                      </div>
                    ) : day.canValidate && !day.isValidated ? (
                      <Button
                        onClick={() => handleValidateDay(day.isYesterday ? 'yesterday' : 'today')}
                        disabled={isValidating || day.isValidated || validateDayMutation.isPending}
                        loading={isValidating || validateDayMutation.isPending}
                        className="w-full"
                        size="sm"
                      >
                        {t('challenges.validate')}
                      </Button>
                    ) : day.isTomorrow ? (
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-text-muted/20 flex items-center justify-center mb-sm">
                          <svg className="w-6 h-6 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-sm text-text-muted">{t('stats.upcoming')}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-text-muted/20 flex items-center justify-center mb-sm">
                          <svg className="w-6 h-6 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-sm text-text-muted">
                          {day.unavailableReason}
                        </span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            
            {/* Time Info */}
            {challenge.frequency.times && challenge.frequency.times.length > 0 && (
              <div className="mt-lg p-md bg-bg-alt/50 rounded-lg">
                <p className="text-sm text-text-muted">
                  <strong>Validation available after:</strong> {challenge.frequency.times[challenge.frequency.times.length - 1]}
                  {challenge.frequency.mode === 'hourly' && ` (${challenge.frequency.endHour || 18}:00)`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
} 